import { useEffect, useState } from 'react';

interface CelebrationBurstProps {
  /** When this becomes true, the burst plays once. Reset to false to re-arm. */
  trigger: boolean;
  /** Big number / dollar shown in the center while the burst plays. */
  centerLabel?: string;
  /** Sub-label (e.g. "+ to your month"). */
  subLabel?: string;
  /** Called once the burst animation finishes (~1.4s). */
  onDone?: () => void;
}

const PARTICLE_COUNT = 14;

/**
 * Confetti-like radial burst with a soft expanding ring + number overlay.
 * Pure CSS — no canvas, no library — keeps the bundle slim.
 *
 * Triggered when an attention item converts (Quoted → Booked, Booked → Done).
 * Real wins → real reward. Same dopamine, no fake gamification.
 */
export function CelebrationBurst({
  trigger,
  centerLabel,
  subLabel,
  onDone,
}: CelebrationBurstProps) {
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!trigger) return;
    setPlaying(true);
    const t = window.setTimeout(() => {
      setPlaying(false);
      onDone?.();
    }, 1400);
    return () => window.clearTimeout(t);
  }, [trigger, onDone]);

  if (!playing) return null;

  return (
    <div
      className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center"
      aria-live="polite"
    >
      {/* Expanding ring */}
      <div
        className="absolute h-32 w-32 rounded-full border-4 border-status-booked"
        style={{ animation: 'ss-burst 700ms cubic-bezier(0.4, 0, 0.2, 1) both' }}
      />
      {/* Particles */}
      {Array.from({ length: PARTICLE_COUNT }).map((_, i) => {
        const angle = (i / PARTICLE_COUNT) * 2 * Math.PI;
        const distance = 80 + Math.random() * 40;
        const dx = Math.cos(angle) * distance;
        const dy = Math.sin(angle) * distance;
        const colors = ['#10B981', '#2563EB', '#F59E0B', '#EC4899'];
        const color = colors[i % colors.length];
        const delay = Math.random() * 80;
        return (
          <span
            key={i}
            className="absolute h-2 w-2 rounded-full"
            style={{
              backgroundColor: color,
              animation: `ss-particle 900ms cubic-bezier(0.4, 0, 0.2, 1) ${delay}ms both`,
              // CSS variables consumed by the keyframes below.
              ['--dx' as string]: `${dx}px`,
              ['--dy' as string]: `${dy}px`,
            }}
          />
        );
      })}
      {/* Center label — wrapped in an opaque rounded card so the text reads
          cleanly against the conversation content underneath (previously it
          rendered raw over the page and crashed into pinned-header chips,
          Ernie 2026-05-22). Particles still burst around it. */}
      {centerLabel && (
        <div
          className="relative flex flex-col items-center text-center rounded-2xl bg-white/95 dark:bg-[#0F172A]/95 px-6 py-4 shadow-ss-md backdrop-blur-md"
          style={{ animation: 'ss-pop 600ms cubic-bezier(0.34, 1.56, 0.64, 1) both' }}
        >
          <span className="text-3xl font-bold text-status-booked drop-shadow-sm">
            {centerLabel}
          </span>
          {subLabel && (
            <span className="mt-1 text-sm font-semibold text-ink-700 dark:text-[#94A3B8]">
              {subLabel}
            </span>
          )}
        </div>
      )}
      {/* Local keyframes for particles — kept inline since they reference
          per-particle CSS variables. */}
      <style>{`
        @keyframes ss-particle {
          0% { transform: translate(0, 0) scale(1); opacity: 1; }
          80% { opacity: 0.9; }
          100% { transform: translate(var(--dx), var(--dy)) scale(0.4); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
